import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { toggle } from '@nullvoxpopuli/ember-composable-helpers';
import { Changeset } from 'ember-changeset';
import type { DetailedChangeset } from 'ember-changeset/types';
import { dropTask } from 'ember-concurrency';
import { not } from 'ember-truth-helpers';
import SlideToggle from 'garaje/components/slide-toggle';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import TaskButton from 'garaje/components/ui/classic/task-button';
import type ConnectLocationConfigurationModel from 'garaje/models/connect-location-configuration';
import type LocationModel from 'garaje/models/location';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface ConnectHostNotificationsComponentSignature {
  Args: {
    location: LocationModel;
    connectLocationConfiguration: ConnectLocationConfigurationModel;
  };
  Element: HTMLDivElement;
}

export default class ConnectHostNotificationsComponent extends Component<ConnectHostNotificationsComponentSignature> {
  @service declare flashMessages: FlashMessagesService;

  @tracked isOpen = false;
  @tracked locationChangeset!: DetailedChangeset<LocationModel>;
  @tracked connectLocationConfigurationChangeset!: DetailedChangeset<ConnectLocationConfigurationModel>;

  get canSave(): boolean {
    return (
      !this.saveTask.isRunning && (this.locationChangeset.isDirty || this.connectLocationConfigurationChangeset.isDirty)
    );
  }

  constructor(owner: unknown, args: ConnectHostNotificationsComponentSignature['Args']) {
    super(owner, args);
    this.#buildChangeset();
  }

  saveTask = dropTask(async () => {
    try {
      if (this.locationChangeset.isDirty) {
        await this.locationChangeset.save();
      }
      if (this.connectLocationConfigurationChangeset.isDirty) {
        await this.connectLocationConfigurationChangeset.save();
      }
      this.flashMessages.showAndHideFlash('success', 'Saved!');

      this.isOpen = false;
      this.#buildChangeset();
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(error));
    }
  });

  @action
  onEditButtonClick(): void {
    this.isOpen = true;
  }

  @action
  cancel(): void {
    this.isOpen = false;
    this.locationChangeset.rollback();
    this.connectLocationConfigurationChangeset.rollback();
  }

  #buildChangeset(): void {
    const { location, connectLocationConfiguration } = this.args;

    this.locationChangeset = Changeset(location);
    this.connectLocationConfigurationChangeset = Changeset(connectLocationConfiguration);
  }

  <template>
    <SettingsPanel data-test-connect-host-notifications @isOpen={{this.isOpen}} ...attributes as |panel|>
      <panel.header
        @iconUri="/assets/images/features/notifications.svg"
        @title="Host notifications"
        @description="Automatically notify employees when their visitors arrive at the property or location."
        @onCancel={{this.cancel}}
        as |header|
      >
        <header.controls>
          {{#if panel.isOpen}}
            <TaskButton
              class="btn btn--small btn--brand"
              @disabled={{not this.canSave}}
              @task={{this.saveTask}}
              @text="Save"
              @runningText="Saving..."
              data-test-save-connect-host-notifications
            />
          {{else}}
            <button
              type="button"
              class="btn btn--small btn--brand"
              {{on "click" this.onEditButtonClick}}
              data-test-edit-connect-host-notifications
            >
              Edit
            </button>
          {{/if}}
        </header.controls>
      </panel.header>
      <panel.body>
        <div class="flex flex-col ml-16 mt-6 items-start">
          <div class="flex items-center">
            <SlideToggle
              class="toggle--tiny mr-2"
              data-test-toggle-property-host-notifications
              @isDisabled={{this.saveTask.isRunning}}
              @isChecked={{this.connectLocationConfigurationChangeset.hostNotificationsEnabled}}
              @onClick={{toggle "hostNotificationsEnabled" this.connectLocationConfigurationChangeset}}
            />
            Notify employees when their visitors arrive at the&nbsp;<b>property</b>
          </div>
          <div class="flex items-center mt-4">
            <SlideToggle
              class="toggle--tiny mr-2"
              data-test-toggle-location-host-notifications
              @isDisabled={{this.saveTask.isRunning}}
              @isChecked={{this.locationChangeset.hostNotificationsEnabled}}
              @onClick={{toggle "hostNotificationsEnabled" this.locationChangeset}}
            />
            Notify employees when their visitors arrive at the&nbsp;<b>location</b>
          </div>
        </div>
      </panel.body>
    </SettingsPanel>
  </template>
}
